import cn from 'classnames';
import { ReactElement, useMemo } from 'react';
import { MdArrowForwardIos } from 'react-icons/md';
import { FullNavItem } from '../../utils/routing/location-constants';
import { useBpmNavigate } from '../../utils/routing/useBpmNavigate';
import { Button } from '../Button';
import { Appearance, Background } from '../designConstants';
import { SubNavMenu } from './SubNavMenu';
import { useNavSubMenu } from './useNavBarPopover';

export const ICON_SPACER = <div style={{ width: 20, height: 20 }} />;

interface NavMenuProps {
  items: FullNavItem[];
}

export const NavMenu = ({ items }: NavMenuProps): ReactElement => (
  <div className='nav-group-menu'>
    {items.map(item => (
      <NavMenuItem key={item.key} item={item} />
    ))}
  </div>
);

interface NavMenuItemProps {
  item: FullNavItem;
}

const NavMenuItem = ({ item }: NavMenuItemProps): ReactElement => {
  const [isSubNavsExpanded, setSubNavsExpanded] = useNavSubMenu(item.key);
  const navigate = useBpmNavigate();
  const { fullPath, prettyName, icon, type } = item;
  const subNavs = useMemo(() => ('subNavs' in item ? item.subNavs : undefined), [item]);

  const onClick = useMemo(() => {
    if (subNavs && subNavs.length > 0) {
      return () => setSubNavsExpanded(!isSubNavsExpanded);
    }

    return () => navigate({ type, path: fullPath.slice(1), prettyName });
  }, [subNavs, setSubNavsExpanded, isSubNavsExpanded, navigate, type, fullPath, prettyName]);

  return (
    <div className='nav-menu-item-container'>
      <Button
        appearance={Appearance.EMPTY}
        background={Background.DARK}
        leadingElement={icon ?? ICON_SPACER}
        trailingElement={
          subNavs && (
            <span className='nav-menu-expander'>
              <MdArrowForwardIos size={14} />
            </span>
          )
        }
        className={cn('nav-menu-item', { 'sub-navs-expanded': isSubNavsExpanded })}
        data-testid={subNavs?.length ? 'nav-item-expandable' : undefined}
        onClick={onClick}
      >
        <span className='nav-menu-label'>{prettyName}</span>
      </Button>
      {subNavs && (
        <SubNavMenu parentPrettyName={prettyName} subNavs={subNavs} expanded={isSubNavsExpanded} />
      )}
    </div>
  );
};
